import {
  ApiError,
  Auth0AccountRole,
  InvitesService,
  InviteType,
} from "client/openapi";
import type { PersonRow } from "client/openapi/models/PersonRow";
import { Button, ButtonColor } from "components/Button";
import { Dialog, DialogContent } from "components/Dialog";
import { useContext, useState } from "react";
import Notifications from "util/notifications";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import { personName } from "./person";

/**
 * A tutor's Account step. Done: they have a Tadpole account. Not yet: send
 * them the invite to create one again, the tutor page's reinvite (it goes
 * to their email, so it waits for one).
 */
export default function AccountStepDialog({
  tutor,
  orgId,
  done,
  onDone,
  onClose,
}: {
  tutor: PersonRow;
  orgId: number;
  done: boolean;
  onDone: () => void;
  onClose: () => void;
}) {
  const { account } = useContext(OrgRolesAndAccountContext);
  const name = personName(tutor) || "The tutor";
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  function send() {
    setSending(true);
    InvitesService.inviteUser({
      requestBody: {
        first_name: tutor.first_name ?? "",
        last_name: tutor.last_name ?? "",
        phone_number: tutor.phone_number ?? undefined,
        email: tutor.email as string,
        org_invited_by: orgId,
        acct_invited_by: account?.reference_id as string,
        type: InviteType.TUTOR,
        user_id: tutor.id,
      },
      role: Auth0AccountRole.ORG_TUTOR,
      createEntity: false,
      createAccount: true,
    })
      .then(() => {
        setSent(true);
        Notifications.success(`Invite sent to ${name}.`);
        onDone();
      })
      .catch((error: ApiError) =>
        Notifications.error(error.body?.detail || "Could not send the invite.")
      )
      .finally(() => setSending(false));
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="dialog-content max-w-[480px]">
        <h2 className="text-lg font-bold">Account</h2>
        {done ? (
          <p className="mt-2 text-sm text-gray-700">
            {name} has a Tadpole account
            {tutor.email ? ` (${tutor.email})` : ""}.
          </p>
        ) : (
          <>
            <p className="mt-2 text-sm text-gray-700">
              {name} hasn't created a Tadpole account yet.{" "}
              {tutor.email
                ? `Send the invite to ${tutor.email} again.`
                : "Add their email on their page to send the invite."}
            </p>
            <div className="mt-4 flex justify-end">
              <Button
                color={
                  !tutor.email || sent ? ButtonColor.GRAY : ButtonColor.GREEN
                }
                disabled={!tutor.email || sending || sent}
                onClick={send}
              >
                {sent ? "Sent" : "Send invite"}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
